import React from 'react';
import { TranslatedString, i18nProps } from '~Types';
import { AggregatedCategoryMap } from '~utilities';
import { CategoriesList, Row, Text } from './View/Styles';
import { Staffs } from '~reducers';

const getCorrections = (report: any) => {
  const corrections = { count: 0, amount: 0 };
  for (const correctionType in report?.lineCorrections) {
    corrections.count += report?.lineCorrections[correctionType].count;
    corrections.amount += report?.lineCorrections[correctionType].amount;
  }
  return corrections;
};

export const getCategoryName = (
  id: string,
  categories: {
    id: string;
    nameLang: TranslatedString;
    parentId?: string | undefined;
  }[],
  i18n: i18nProps,
) => {
  const category = categories?.find((cat) => cat.id === id);
  return category ? i18n(category.nameLang) : id;
};

export const getStaffName = (id: string, staffs: Staffs) => {
  if (staffs.length === 0) return id;
  const staff = staffs.find((s) => s.id === id);
  return staff ? staff.name : id;
};

export const getNorwaySpecificVariables = (report: any) => {
  const corrections = getCorrections(report);
  const linesEntered = report?.totalSales + corrections.amount + report.discountsAmount;
  const turnover = linesEntered - corrections.amount - report.discountsAmount;

  return { linesEntered, turnover };
};

export function displayAggregatedCategoriesInPrint(
  categoriesToDisplay: AggregatedCategoryMap,
  level = 0,
  formatCurrency: (number: number) => void,
  i18n: i18nProps,
) {
  let lines: string[] = [];

  Object.entries(categoriesToDisplay).forEach(([categoryId, category]) => {
    // Skip categories with count 0
    if (category.count === 0) return;

    const indent = ' '.repeat(level * 2); // Adjust indent based on nesting level
    const name = category.name;
    const count = category.count;
    const amount = formatCurrency(category.amount); // Make sure you have implemented formatCurrency

    // Prepare the line for the current category
    const line = `${indent}${count}x ${name} ${amount}`;
    lines.push(line);

    // Process subcategories, if any
    if (typeof category.subCategory === 'object' && Object.keys(category.subCategory).length > 0) {
      // Check and process each subcategory
      Object.entries(category.subCategory).forEach(([subCategoryId, subCategory]) => {
        // Skip "(Direct)" subcategory if its count equals the parent category's count
        if (
          subCategory.name?.endsWith(`(${i18n('Report.AggregatedCategories.Direct')})`) &&
          subCategory.count === category.count
        ) {
          return;
        }

        // Recursively process subcategories
        const subCategoryLines = displayAggregatedCategoriesInPrint(
          { [subCategoryId]: subCategory },
          level + 1,
          formatCurrency,
          i18n,
        );
        lines = lines.concat(subCategoryLines);
      });
    }
  });

  return lines.join('\n');
}

type DisplayAggregatedCategoriesProps = {
  categoriesToDisplay: AggregatedCategoryMap;
  formatCurrency: (number: number) => string;
  i18n: i18nProps;
};

export const DisplayAggregatedCategories: React.FC<DisplayAggregatedCategoriesProps> = ({
  categoriesToDisplay,
  formatCurrency,
  i18n,
}) => {
  const renderCategories = (
    categories: AggregatedCategoryMap,
    level = 0,
  ): (JSX.Element | null)[] => {
    return Object.entries(categories)
      .map(([categoryId, category]) => {
        // Skip categories with count 0
        if (category.count === 0) return null;

        const subCategories =
          category.subCategory && Object.keys(category.subCategory).length > 0
            ? renderCategories(category.subCategory, level + 1)
            : null;

        // Skip "(Direct)" subcategory if its count equals the parent category's count
        const directSubCategoryExists =
          category.subCategory &&
          Object.entries(category.subCategory).some(
            ([, subCategory]) =>
              subCategory.name?.endsWith(`(${i18n('Report.AggregatedCategories.Direct')})`) &&
              subCategory.count === category.count,
          );

        return (
          <div key={categoryId}>
            <Row key={categoryId}>
              <Text>{category.name}</Text>
              <Text>{category.count}</Text>
              <Text>{formatCurrency(category.amount)}</Text>
            </Row>
            {!directSubCategoryExists && subCategories && <ul>{subCategories}</ul>}
          </div>
        );
      })
      .filter(Boolean);
  };

  return <CategoriesList>{renderCategories(categoriesToDisplay)}</CategoriesList>;
};
